import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSubscriptionStatus } from '../hooks/useSubscriptionStatus';

const SubscriptionSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [verificationState, setVerificationState] = useState({
    isVerifying: true,
    attempt: 0,
    maxAttempts: 4,
    message: 'Verifying your subscription...',
    error: null
  });
  const { syncWithStripe, isSubscribed, isLoading } = useSubscriptionStatus();
  
  const sessionId = searchParams.get('session_id');
  const success = searchParams.get('success');

  useEffect(() => {
    const verifySubscriptionWithRetry = async () => {
      if (!success && !sessionId) {
        navigate('/dashboard');
        return;
      }

      const attemptSync = async (attemptNumber) => {
        try {
          console.log(`Sync attempt ${attemptNumber + 1}/${verificationState.maxAttempts}`);
          
          setVerificationState(prev => ({
            ...prev,
            attempt: attemptNumber + 1,
            message: attemptNumber === 0 
              ? 'Verifying your subscription...'
              : 'Almost ready, just a moment longer...',
            error: null
          }));

          const result = await syncWithStripe();
          
          // Check if we now have a subscription
          if (result?.subscription?.id) {
            console.log('Subscription found:', result.subscription);
            setVerificationState(prev => ({
              ...prev,
              isVerifying: false,
              message: 'Subscription verified successfully!'
            }));
            return true;
          }
          
          // If we don't have a subscription yet and have more attempts, retry
          if (attemptNumber < verificationState.maxAttempts - 1) {
            const delay = [0, 3000, 6000, 10000][attemptNumber + 1]; // 0s, 3s, 6s, 10s
            console.log(`No subscription found yet, retrying in ${delay/1000}s...`);
            
            setVerificationState(prev => ({
              ...prev,
              message: `Finalizing your subscription... Retry in ${delay/1000}s`
            }));
            
            setTimeout(() => attemptSync(attemptNumber + 1), delay);
            return false;
          } else {
            // Max attempts reached
            console.log('Max attempts reached, showing fallback UI');
            setVerificationState(prev => ({
              ...prev,
              isVerifying: false,
              error: 'verification_timeout',
              message: 'Your payment was successful, but verification is taking longer than expected.'
            }));
            return false;
          }
          
        } catch (error) {
          console.error(`Sync attempt ${attemptNumber + 1} failed:`, error);
          
          // If we have more attempts, retry
          if (attemptNumber < verificationState.maxAttempts - 1) {
            const delay = [0, 3000, 6000, 10000][attemptNumber + 1];
            console.log(`Retrying in ${delay/1000}s...`);
            
            setVerificationState(prev => ({
              ...prev,
              message: `Connection issue, retrying in ${delay/1000}s...`
            }));
            
            setTimeout(() => attemptSync(attemptNumber + 1), delay);
            return false;
          } else {
            // Max attempts reached with error
            setVerificationState(prev => ({
              ...prev,
              isVerifying: false,
              error: 'sync_failed',
              message: 'Unable to verify subscription status. Please refresh the page or contact support.'
            }));
            return false;
          }
        }
      };

      // Start the sync process
      await attemptSync(0);
    };

    verifySubscriptionWithRetry();
  }, [sessionId, success, syncWithStripe, navigate, verificationState.maxAttempts]);

  useEffect(() => {
    // Once verification is complete and user is subscribed, redirect after a delay
    if (!verificationState.isVerifying && !isLoading && isSubscribed) {
      setTimeout(() => {
        navigate('/dashboard');
      }, 3000);
    }
  }, [verificationState.isVerifying, isLoading, isSubscribed, navigate]);

  const handleContinue = () => {
    navigate('/dashboard');
  };

  const handleManualRefresh = async () => {
    setVerificationState(prev => ({
      ...prev,
      isVerifying: true,
      attempt: 0,
      message: 'Refreshing subscription status...',
      error: null
    }));

    try {
      const result = await syncWithStripe();
      if (result?.subscription?.id) {
        setVerificationState(prev => ({
          ...prev,
          isVerifying: false,
          message: 'Subscription verified successfully!'
        }));
      } else {
        setVerificationState(prev => ({
          ...prev,
          isVerifying: false,
          error: 'no_subscription',
          message: 'No active subscription found. Please contact support if you believe this is an error.'
        }));
      }
    } catch (error) {
      setVerificationState(prev => ({
        ...prev,
        isVerifying: false,
        error: 'refresh_failed',
        message: 'Failed to refresh status. Please try again or contact support.'
      }));
    }
  };

  if (verificationState.isVerifying || isLoading) {
    return (
      <div className="min-h-screen bg-klorah-background flex items-center justify-center p-4">
        <div className="text-center max-w-md mx-auto">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-klorah-primary mx-auto mb-6"></div>
          <h2 className="text-2xl font-semibold text-white mb-2">
            {verificationState.message}
          </h2>
          <p className="text-klorah-text mb-4">
            Please wait while we confirm your payment and activate your account.
          </p>
          {verificationState.attempt > 0 && (
            <div className="glass-panel p-4">
              <p className="text-sm text-klorah-text-light">
                Verification attempt {verificationState.attempt} of {verificationState.maxAttempts}
              </p>
              <div className="w-full bg-klorah-border rounded-full h-2 mt-2">
                <div 
                  className="bg-klorah-primary h-2 rounded-full transition-all duration-500"
                  style={{ width: `${(verificationState.attempt / verificationState.maxAttempts) * 100}%` }}
                ></div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Handle verification timeout or error states
  if (verificationState.error && !isSubscribed) {
    return (
      <div className="min-h-screen bg-klorah-background flex items-center justify-center p-4">
        <div className="text-center max-w-md mx-auto">
          {/* Warning Icon */}
          <div className="w-20 h-20 bg-yellow-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-10 h-10 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 19c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>

          {/* Status Message */}
          <h2 className="text-2xl font-semibold text-white mb-4">
            Payment Verification Delayed
          </h2>
          
          <p className="text-klorah-text text-lg mb-6 leading-relaxed">
            {verificationState.message}
          </p>

          {/* Action Buttons */}
          <div className="space-y-4">
            <button
              onClick={handleManualRefresh}
              className="w-full py-4 px-6 bg-klorah-primary text-black font-semibold rounded-xl hover:bg-klorah-primary/90 transition-all duration-200"
            >
              Refresh Status
            </button>
            
            <button
              onClick={handleContinue}
              className="w-full py-3 px-6 bg-klorah-card border border-klorah-border text-klorah-text hover:text-white hover:bg-klorah-border rounded-xl transition-all duration-200"
            >
              Continue to Dashboard
            </button>
          </div>

          {/* Help Text */}
          <p className="text-sm text-klorah-text-light mt-6">
            Your payment was processed successfully. If issues persist, please contact support with session ID: {sessionId || 'N/A'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-klorah-background flex items-center justify-center p-4">
      <div className="text-center max-w-md mx-auto">
        {/* Success Icon */}
        <div className="w-20 h-20 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
          <svg className="w-10 h-10 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>

        {/* Success Message */}
        <h1 className="text-3xl font-bold text-white mb-4">
          Welcome to Sessions Pro!
        </h1>
        
        <p className="text-klorah-text text-lg mb-8 leading-relaxed">
          Your subscription has been activated successfully. You now have access to all premium features including unlimited workshop hosting, calendar integration, and advanced booking management.
        </p>

        {/* Features List */}
        <div className="glass-panel p-6 mb-8 text-left">
          <h3 className="text-lg font-semibold text-white mb-4">Your Pro features are now active:</h3>
          <div className="space-y-3">
            {[
              'Unlimited workshop hosting',
              'Calendar integration',
              'Automated email confirmations',
              'Workshop analytics',
              'Priority support'
            ].map((feature, index) => (
              <div key={index} className="flex items-center space-x-3">
                <div className="w-5 h-5 rounded-full bg-klorah-primary/20 flex items-center justify-center flex-shrink-0">
                  <svg className="w-3 h-3 text-klorah-primary" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <span className="text-klorah-text">{feature}</span>
              </div>
            ))}
          </div>
        </div>

        {/* CTA Button */}
        <button
          onClick={handleContinue}
          className="w-full py-4 px-6 bg-klorah-primary text-black font-semibold rounded-xl hover:bg-klorah-primary/90 transition-all duration-200"
        >
          Continue to Dashboard
        </button>

        {/* Auto redirect notice */}
        <p className="text-sm text-klorah-text-light mt-4">
          You'll be automatically redirected to your dashboard in a few seconds.
        </p>
      </div>
    </div>
  );
};

export default SubscriptionSuccess; 